import {Component, HostBinding, OnInit} from '@angular/core';
import {MatRadioChange} from '@angular/material/radio';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {PlanningItemHelper} from '@application/helper/planning-prototype/planning-item-helper';
import {Maintenance} from '@domain/planning-prototype/maintenance';
import {MaintenanceType} from '@domain/planning-prototype/maintenance-type';
import {PlanningEquipment} from '@domain/planning-prototype/planning-equipment';
import {PlanningItem} from '@domain/planning-prototype/planning-item';
import {PlanningItemFactory} from '@domain/planning-prototype/planning-item-factory';
import {AssertionUtils, BaseComponent, TranslateService} from '@vdw/angular-component-library';
import {filter, takeUntil} from 'rxjs';
import {PlanningPrototypeContextService} from '../../utilities/planning-prototype-context.service';
import {NavigationPlanningItemSidebar} from './navigation-item-sidebar.interface';

@Component({
  selector: 'app-planning-item-sidebar',
  templateUrl: './planning-item-sidebar.component.html',
  styleUrls: ['./planning-item-sidebar.component.scss']
})
export class PlanningItemSidebarComponent extends BaseComponent implements OnInit {
  public planningItem: PlanningItem;
  public machine: PlanningEquipment;
  public createItemMode = false;
  public maintenanceTypes = [MaintenanceType.MAINTENANCE, MaintenanceType.MACHINE_UNAVAILABLE];

  @HostBinding('class.wide')
  public get isWide(): boolean {
    return PlanningItemHelper.isRunItem(this.planningItem) && AssertionUtils.isNullOrUndefined(this.planningItem.actualEndDate);
  }

  private readonly KEY_EDIT_OBJECT = 'GENERAL.ACTIONS.EDIT_OBJECT';

  public constructor(
    private readonly planningContext: PlanningPrototypeContextService,
    private readonly translate: TranslateService,
    private readonly navigationHelperService: NavigationHelperService<NavigationPlanningItemSidebar>
  ) {
    super();
  }

  public ngOnInit(): void {
    this.planningContext.selectedItemChanges
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((item: PlanningEquipment | PlanningItem) => item instanceof PlanningItem)
      )
      .subscribe((item: PlanningItem) => {
        this.planningItem = item;
        if (!AssertionUtils.isNullOrUndefined(this.planningContext.machinesByPlanningItem[item.draftId])) {
          this.machine = this.planningContext.machinesByPlanningItem[item.draftId];
          this.maintenanceTypes = PlanningItemHelper.getMaintenanceTypesForMachineType(this.machine.equipmentKind);
        }
        this.createItemMode = item === this.planningContext.itemCreationPlaceholder;
      });
    let obj: NavigationPlanningItemSidebar = this.navigationHelperService.getPartialState(Object.keys(this.planningContext.getEmptyDataOfPlanningItemSidebarForNavigationHelper()));
    if (!AssertionUtils.isNullOrUndefined(obj)) {
      this.planningItem = obj.planningItem;
      this.machine = obj.machine;
      this.createItemMode = obj.createItemMode;
      this.maintenanceTypes = obj.maintenanceTypes;
    }
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState({
      planningItem: this.planningItem,
      machine: this.machine,
      createItemMode: this.planningContext.itemCreationPlaceholder,
      maintenanceTypes: this.maintenanceTypes
    });
  }

  public closeMachineDetail(): void {
    this.planningContext.selectItem(null);
  }

  public getFormTitle(): string {
    if (this.createItemMode) {
      return this.translate.instant('GENERAL.ACTIONS.ADD_TO_OBJECT', {object: 'PLANNING.PLANNING'});
    }

    if (this.planningItem instanceof Maintenance) {
      return this.translate.instant(this.KEY_EDIT_OBJECT, {object: `PLANNING.ADD_ITEM.TYPES.${this.planningItem.maintenanceType}`}) + ' ' + this.machine.name;
    }

    if (PlanningItemHelper.canBeInRun(this.planningItem)) {
      return PlanningItemHelper.getItemName(this.planningItem);
    }

    if (PlanningItemHelper.isRunItem(this.planningItem)) {
      const run = this.translate.instant('PLANNING.ADD_ITEM.TYPES.RUN').toLowerCase();
      return this.translate.instant(this.KEY_EDIT_OBJECT, {object: run}) + ' ' + this.machine.name;
    }

    return '';
  }

  public getSelectedItemType(): MaintenanceType | 'RUN' {
    if (PlanningItemHelper.isRunItem(this.planningItem)) {
      return 'RUN';
    }
    return (this.planningItem as Maintenance).maintenanceType;
  }

  public selectedItemTypeChanged(event: MatRadioChange): void {
    if (this.maintenanceTypes.includes(event.value)) {
      if (!(this.planningItem instanceof Maintenance)) {
        this.updateItemCreatePlaceholderToMaintenance(event.value);
      } else {
        this.planningItem.maintenanceType = event.value;
      }
      return;
    }

    if (event.value !== 'RUN' || PlanningItemHelper.isRunItem(this.planningItem)) {
      return;
    }
    this.updateItemCreatePlaceholderToRun();
  }

  private updateItemCreatePlaceholderToRun(): void {
    this.planningContext.itemCreationPlaceholder = PlanningItemFactory.createRunForMachine(
      {
        draftId: this.planningItem.draftId,
        sequenceNumber: this.planningItem.sequenceNumber,
        earliestStartDate: this.planningItem.earliestStartDate,
        minimumDuration: this.planningItem.minimumDuration
      },
      this.machine
    );
    this.planningContext.selectItem(this.planningContext.itemCreationPlaceholder);
  }

  private updateItemCreatePlaceholderToMaintenance(type: MaintenanceType): void {
    this.planningContext.itemCreationPlaceholder = new Maintenance(
      {
        draftId: this.planningItem.draftId,
        sequenceNumber: this.planningItem.sequenceNumber,
        earliestStartDate: this.planningItem.earliestStartDate,
        minimumDuration: this.planningItem.minimumDuration
      },
      type
    );
    this.planningContext.selectItem(this.planningContext.itemCreationPlaceholder);
  }
}
